import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { api } from "../lib/api";

function exportCsv(data) {
  const rows = [["A.C. HUB — Statistiche sito"], ["Generato", new Date().toLocaleString("it-IT")], []];
  rows.push(["Riepilogo"]);
  rows.push(["Visite oggi", data.today]);
  rows.push(["Visite ultimi 7 giorni", data.last7]);
  rows.push(["Visite ultimi 30 giorni", data.last30]);
  rows.push(["Sessioni uniche (30 giorni)", data.sessions30]);
  rows.push([]);
  rows.push(["Fonte di arrivo", "Visite"]);
  data.sources.forEach((s) => rows.push([s.label, s.count]));
  rows.push([]);
  rows.push(["Pagina più vista", "Visite"]);
  data.pages.forEach((p) => rows.push([p.label, p.count]));
  rows.push([]);
  rows.push(["Dispositivo", "Visite"]);
  data.devices.forEach((d) => rows.push([d.label, d.count]));
  rows.push([]);
  rows.push(["Fonte delle richieste", "Richieste"]);
  data.leads_by_source.forEach((l) => rows.push([l.label, l.count]));
  rows.push([]);
  rows.push(["Giorno", "Visite"]);
  data.daily.forEach((d) => rows.push([d.day, d.count]));
  const csv = "\uFEFF" + rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `achub-statistiche-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function BarRows({ title, rows, testid, invertLabel = false }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div data-testid={testid}>
      <p className="mb-4 text-[9px] font-bold tracking-[0.22em] text-gold">{title}</p>
      {rows.length === 0 && <p className="text-[12px] text-[#777]">Nessun dato, per ora.</p>}
      <div className="grid gap-2.5">
        {rows.map((r) => (
          <div key={r.label} className="grid items-center gap-2" style={{ gridTemplateColumns: invertLabel ? "1fr auto" : "130px 1fr auto" }}>
            <span className="truncate text-[11px] text-[#c9c6bf]" title={r.label}>{r.label}</span>
            <div className="hidden h-1.5 bg-[#242321] sm:block">
              <div className="h-full bg-gold" style={{ width: `${Math.round((r.count / max) * 100)}%` }} />
            </div>
            <span className="text-right font-mono text-[11px] text-gold-light">{r.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DailyChart({ days }) {
  const max = Math.max(1, ...days.map((d) => d.count));
  return (
    <div data-testid="stats-daily">
      <p className="mb-4 text-[9px] font-bold tracking-[0.22em] text-gold">VISITE · ULTIMI 14 GIORNI</p>
      <div className="flex h-24 items-end gap-1.5">
        {days.map((d, i) => (
          <div key={d.day} className="flex h-full flex-1 flex-col justify-end" title={`${d.day}: ${d.count} visite`}>
            <div
              className="w-full bg-[#c9a45c] transition-all duration-500"
              style={{ height: `${Math.max(4, Math.round((d.count / max) * 100))}%`, opacity: i === days.length - 1 ? 1 : 0.55 }}
            />
          </div>
        ))}
      </div>
      <div className="mt-2 flex gap-1.5">
        {days.map((d, i) => (
          <span key={d.day} className="flex-1 text-center text-[8px] text-[#777]">
            {i % 2 === 0 ? d.day.slice(8) : ""}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function SiteStats() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get("/analytics")
      .then(({ data: d }) => setData(d))
      .catch(() => setData(false));
  }, []);

  if (data === null) {
    return (
      <div className="mt-10 flex items-center gap-3 border border-[#c9a45c]/18 bg-[#101010] p-6 text-[12px] text-[#929292]" data-testid="stats-loading">
        <Loader2 size={14} className="animate-spin text-gold" /> Caricamento statistiche…
      </div>
    );
  }

  if (data === false) return null;

  const cards = [
    { label: "VISITE OGGI", value: data.today },
    { label: "VISITE · 7 GIORNI", value: data.last7 },
    { label: "VISITE · 30 GIORNI", value: data.last30 },
    { label: "SESSIONI UNICHE", value: data.sessions30 },
  ];

  return (
    <div className="mt-8 border border-[#c9a45c]/25 bg-[#0d0d0d] p-6 sm:p-8" data-testid="site-stats">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="eyebrow">Statistiche sito — ultimi 30 giorni</p>
        <button
          data-testid="export-stats-button"
          onClick={() => exportCsv(data)}
          className="btn btn-outline !min-h-[36px] !px-4 !text-[9px]"
        >
          <Download size={12} />
          Esporta statistiche (CSV)
        </button>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="border border-[#c9a45c]/18 bg-[#101010] p-4" data-testid={`stat-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="font-serif text-2xl text-gold-light">{c.value}</div>
            <div className="mt-1 text-[8px] tracking-[0.18em] text-[#929292]">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2" data-testid="stats-weeks">
        {(data.weeks || []).map((w, i) => {
          const prev = data.weeks[1] ? data.weeks[1].count : 0;
          const delta = i === 0 && prev > 0 ? Math.round(((w.count - prev) / prev) * 100) : null;
          return (
            <div key={w.label} className={`border p-4 ${i === 0 ? "border-gold/50 bg-[#c9a45c]/8" : "border-[#c9a45c]/18 bg-[#101010]"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[9px] tracking-[0.18em] text-[#929292]">{w.label.toUpperCase()}</span>
                {delta !== null && (
                  <span className={`text-[11px] font-bold ${delta >= 0 ? "text-[#7ec98c]" : "text-[#d58c8c]"}`} data-testid="week-delta">
                    {delta >= 0 ? "+" : ""}{delta}% rispetto alla settimana prima
                  </span>
                )}
              </div>
              <div className="mt-2 font-serif text-3xl text-cream">{w.count}</div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 grid gap-8 md:grid-cols-2">
        <BarRows title="FONTI DI ARRIVO" rows={data.sources} testid="stats-sources" />
        <BarRows title="PAGINE PIÙ VISTE" rows={data.pages} testid="stats-pages" />
      </div>

      <div className="mt-8 grid gap-8 md:grid-cols-3">
        <BarRows title="DISPOSITIVI" rows={data.devices} testid="stats-devices" />
        <BarRows title="RICHIESTE PER FONTE" rows={data.leads_by_source} testid="stats-leads-source" />
        <DailyChart days={data.daily} />
      </div>
    </div>
  );
}
